import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, logEducatorDashboard, LogLevel } from "utils";
import { trackClickedButton } from "utils/analytics";
import {
  useClassroomLessonAssignment,
  createQuestionSet,
  getQuestionSet,
  applyQuestionSet,
  patchGroupAssignments,
  fetchAllScopedQuestions,
  fetchScopedQuestionsPage,
} from "hooks/useCurriculum/useCurriculum";
import { useQuestionFacets, useStandardQuestionFacets } from "hooks/useQuestionFacets/useQuestionFacets";
import { request } from "hooks/useOkoApi/useOkoApi";
import { QuestionFilterPanel, AutoAssignCheckbox } from "components/pages/LessonsStandards/QuestionFilterPanel";
import {
  EMPTY_QUESTION_FILTER,
  fromQuestionFilter,
  serializeQuestionFilter,
  isNoOpFilter,
} from "components/pages/LessonsStandards/questionFilters";
import { ConsensusActivityRender } from "components/pages/Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { buildPreviewChallenge } from "utils/challenge";
import { UpdateQuestionSetModal, ReuseQuestionSetModal } from "./QuestionSetModals";
import { compareCode } from "components/pages/Educator/Dashboard/assignmentDraft";

// Questions load a page at a time as the grid is scrolled (the server caps a
// page at 50; 20 keeps the first paint cheap).
const BANK_PAGE_SIZE = 20;

// The set stores references; the wire enum derives from the question's own shape.
export const toChallengeRef = (q) => ({
  challenge_id: q.id,
  challenge_type: q.answer_options?.length ? "multiplechoice" : "openresponse",
});

const Page = styled.div`
  background: ${colors.milkGlass500};
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 100vh;
  padding: 32px 32px 112px;
`;

const HeaderRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Eyebrow = styled.span`
  color: ${colors.orange750};
  font-family: "ABC Marfa", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 20px;
  text-transform: uppercase;
`;

const PageTitle = styled.h1`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
`;

const NarrativeHeading = styled.p`
  color: ${colors.nightlyWoods500};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
  margin: 8px 0 0;
`;

const NarrativeText = styled.p`
  color: ${colors.nightlyWoods500};
  font-size: 16px;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
  white-space: pre-wrap;

  ${({ $clamped }) => $clamped && `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  `}
`;

const GoalsList = styled.ul`
  color: ${colors.nightlyWoods500};
  font-size: 16px;
  letter-spacing: 0.25px;
  line-height: 22px;
  list-style: disc;
  margin: 0;
  padding-left: 24px;
`;

const ShowMoreButton = styled.button`
  align-self: flex-start;
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  font-size: 16px;
  line-height: 22px;
  padding: 0;
  text-align: left;
`;

// Figma 5136-70710: the tabs and body sit straight on the off-white page — no
// white surface, no rounding.
const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

// Tabs (Figma 5136-70728): active = blue text + 3px bottom border.
const TabBar = styled.div`
  border-bottom: 1px solid #acafad;
  display: flex;
  padding: 0 24px;
`;

const TabButton = styled.button`
  background: transparent;
  border: none;
  border-bottom: 3px solid ${(p) => (p.$active ? colors.blue500 : "transparent")};
  color: ${(p) => (p.$active ? colors.blue700 : colors.nightlyWoods500)};
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 22px;
  padding: 24px 16px;
  white-space: nowrap;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
`;

const CountRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  min-height: 48px;
`;

const CountText = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const SecondaryButton = styled.button`
  background: white;
  border: 1px solid ${colors.blue500};
  border-radius: 8px;
  color: ${colors.blue500};
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:hover:not(:disabled) {
    background: ${colors.blue50};
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
`;

// Question thumbnail (Figma 5136-70751): off-white bordered card, preview on
// top, heart action pinned bottom-left.
const Thumb = styled.div`
  background: ${colors.milkGlass500};
  border: 1px solid #acafad;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 16px 16px;
`;

// Sits under the grid; scrolling it into view asks for the next page.
const LoadMoreTrigger = styled.div`
  height: 20px;
  width: 100%;
`;

// The preview is inert: with disableOptions a click on an answer pops a "not here"
// tooltip, which is noise in a grid of thumbnails. The enlarge control is the one
// interaction the preview does offer, so it keeps its clicks.
const ThumbStage = styled.div`
  display: flex;
  justify-content: center;
  pointer-events: none;

  /* Without this the preview's automatic minimum size is its content width, so a
     wide question image centres itself outside the card instead of shrinking. */
  > * {
    min-width: 0;
  }

  [role="button"][aria-label="Enlarge question image"] {
    pointer-events: auto;
  }
`;

const HeartButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.blue700};
  cursor: pointer;
  display: inline-flex;
  font-size: 18px;
  font-weight: ${(p) => (p.$added ? 700 : 400)};
  gap: 8px;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px 0 8px;
`;

const HeartIcon = ({ filled }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

// Empty state (Figma 5136-71197).
const EmptyState = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 48px 0;
  text-align: center;
`;

const EmptyHeading = styled.p`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
`;

const EmptyBody = styled.p`
  color: ${colors.nightlyWoods500};
  font-size: 16px;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
  max-width: 504px;
`;

const EmptyHeartIcon = () => (
  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke={colors.nightlyWoods500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    <path d="M15.8 9.2a2.5 2.5 0 0 0-3.5 0l-.3.3-.3-.3a2.5 2.5 0 0 0-3.5 3.6l3.8 3.6 3.8-3.6a2.5 2.5 0 0 0 0-3.6" />
  </svg>
);

// Fixed save bar (Figma 5136-70826).
// Both CTAs sit together on the right (Figma 5136-70946).
const SaveBar = styled.div`
  align-items: center;
  background: ${colors.gray50};
  border-top: 1px solid ${colors.blue100};
  bottom: 0;
  box-shadow: 0 -1px 1.5px rgba(0, 0, 0, 0.05);
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  left: 0;
  padding: 16px 32px;
  position: fixed;
  right: 0;
  z-index: 10;
`;

const PrimaryButton = styled.button`
  background: ${colors.blue500};
  border: none;
  border-radius: 8px;
  color: ${colors.milkGlass500};
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 20px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StateMessage = styled.div`
  color: ${colors.milkGlass900};
  padding: 48px;
  text-align: center;
`;

const PieIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.nightlyWoods500} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </svg>
);

// "Select My Question Set" (Figma 5191-100708): full-bank grid with per-question
// add/remove, a My Question Set tab, and Save & Exit persisting an immutable
// QuestionSet the group code points at.
export const QuestionSetView = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const classroomId = params.get("classroomId");
  const groupCode = params.get("groupCode");
  const assignmentIdParam = params.get("assignmentId");
  const classId = params.get("classId");

  const { assignment: envelope, loading: envelopeLoading } = useClassroomLessonAssignment(classroomId);

  // Same bundle resolution as LessonEditGroupFocus: assignmentId wins, then the
  // bundle owning this group code.
  const lessonEntry = useMemo(() => {
    const lessons = envelope?.lessons || [];
    const byAssignment = assignmentIdParam && lessons.find((l) => l.assignment_id === assignmentIdParam);
    if (byAssignment) return byAssignment;
    return lessons.find((l) => (l.assignments || []).some((a) => a.group_code === groupCode)) || null;
  }, [envelope, assignmentIdParam, groupCode]);
  // A quick group is not in the bundle envelope at all — it is a standalone group
  // row — so when the code doesn't belong to a bundle, look it up in the classroom's
  // quick groups and adapt it into the same row shape the rest of the page reads.
  const [quickGroup, setQuickGroup] = useState(null);
  useEffect(() => {
    if (!classroomId || !groupCode || lessonEntry || envelopeLoading) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await request(`quick-groups/classrooms/${classroomId}`, { method: "GET" });
        const found = (data?.quick_groups || data?.groups || []).find((g) => g.code === groupCode);
        if (!cancelled) setQuickGroup(found || null);
      } catch {
        /* leave null; the page shows its no-scope state */
      }
    })();
    return () => { cancelled = true; };
  }, [classroomId, groupCode, lessonEntry, envelopeLoading]);

  const lesson = lessonEntry?.lesson || null;
  const bundleAssignments = useMemo(() => lessonEntry?.assignments || [], [lessonEntry]);
  const row = useMemo(() => {
    const bundleRow = bundleAssignments.find((a) => a.group_code === groupCode);
    if (bundleRow) return bundleRow;
    if (!quickGroup) return null;
    // A quick group runs no track, and its standard is the group's own.
    return {
      group_code: quickGroup.code,
      track_type: null,
      standard_code: quickGroup.standard_code,
      standard_name: quickGroup.standard_name,
      question_filter: quickGroup.question_filter || null,
      question_set_id: quickGroup.question_set_id || null,
    };
  }, [bundleAssignments, groupCode, quickGroup]);
  const assignmentId = assignmentIdParam || lessonEntry?.assignment_id || null;
  const lessonId = lesson?.id || null;
  const trackType = row?.track_type || null;
  const standardCode = row?.standard_code || null;
  // Prereq/extension pin their one standard; core spans the lesson's set.
  const pinnedStandardCode = trackType === "core" ? null : standardCode;

  const [tab, setTab] = useState("bank");
  const [filter, setFilter] = useState(EMPTY_QUESTION_FILTER);
  const [filterSeeded, setFilterSeeded] = useState(false);
  useEffect(() => {
    if (row && !filterSeeded) {
      setFilter(fromQuestionFilter(row.question_filter) || EMPTY_QUESTION_FILTER);
      setFilterSeeded(true);
    }
  }, [row, filterSeeded]);

  // Ordered selection of {challenge_id, challenge_type}; the save sends it verbatim.
  const [selection, setSelection] = useState([]);
  const selectedIds = useMemo(() => new Set(selection.map((s) => s.challenge_id)), [selection]);

  // Seed the selection from the group's saved set (bundle payload carries the id).
  const questionSetId = row?.question_set_id || null;
  const [setSeeded, setSetSeeded] = useState(false);
  useEffect(() => {
    if (setSeeded || !row) return;
    if (!questionSetId) { setSetSeeded(true); return; }
    let cancelled = false;
    getQuestionSet(questionSetId)
      .then((set) => {
        if (cancelled) return;
        setSelection((set?.questions || []).map((q) => ({
          challenge_id: q.challenge_id,
          challenge_type: q.challenge_type,
        })));
        setSetSeeded(true);
      })
      .catch((e) => {
        logEducatorDashboard("QuestionSetView: failed to load question set", {
          level: LogLevel.WARNING,
          event: "question_set_load_error",
          statusCode: e?.status,
          questionSetId,
        });
        if (!cancelled) setSetSeeded(true);
      });
    return () => { cancelled = true; };
  }, [row, questionSetId, setSeeded]);

  // Every question ever fetched, by id — the My Question Set tab joins against it.
  const questionsByIdRef = useRef(new Map());
  const [bankQuestions, setBankQuestions] = useState([]);
  const [bankTotal, setBankTotal] = useState(0);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankLoadingMore, setBankLoadingMore] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [bankMoreError, setBankMoreError] = useState(null);
  const [, setJoinVersion] = useState(0);

  const scopeReady = Boolean(row && (lessonId || standardCode) && filterSeeded);
  const activeFilter = useMemo(() => ({
    standardCode: pinnedStandardCode || undefined,
    standards: pinnedStandardCode ? undefined : filter.standards,
    learningComponents: filter.learningComponents,
    questionType: filter.questionType,
  }), [pinnedStandardCode, filter]);
  const filterKey = JSON.stringify(activeFilter);

  // Paging lives in a ref so the observer can guard re-entry synchronously — two
  // sentinel hits in the same frame must not fire the same page twice. A filter
  // change swaps in a fresh object, so a response from the previous filter fails
  // the identity check and is dropped instead of appended to the new list.
  const bankPagingRef = useRef({ offset: 0, loading: false, done: false });

  const loadNextBankPage = useCallback(() => {
    const paging = bankPagingRef.current;
    if (!scopeReady || paging.loading || paging.done) return;
    const { offset } = paging;
    paging.loading = true;
    if (offset === 0) setBankLoading(true); else setBankLoadingMore(true);
    fetchScopedQuestionsPage({ lessonId, standardCode, filter: activeFilter, limit: BANK_PAGE_SIZE, offset })
      .then(({ questions, total }) => {
        if (bankPagingRef.current !== paging) return;
        paging.loading = false;
        paging.offset = offset + questions.length;
        paging.done = questions.length === 0 || paging.offset >= total;
        questions.forEach((q) => questionsByIdRef.current.set(q.id, q));
        setJoinVersion((v) => v + 1);
        setBankQuestions((prev) => (offset === 0 ? questions : [...prev, ...questions]));
        setBankTotal(total);
        setBankLoading(false);
        setBankLoadingMore(false);
      })
      .catch((e) => {
        if (bankPagingRef.current !== paging) return;
        paging.loading = false;
        // Stop paging: the sentinel stays on screen, so leaving it live would
        // retry a failing endpoint on every scroll.
        paging.done = true;
        logEducatorDashboard("QuestionSetView: failed to load questions", {
          level: LogLevel.WARNING,
          event: "bank_load_error",
          statusCode: e?.status,
          lessonId,
          standardCode,
          offset,
        });
        const message = e?.detail || e?.message || "Failed to load questions.";
        // A failed first page is the whole view; a failed later page must not
        // throw away the questions already on screen.
        if (offset === 0) setBankError(message);
        else setBankMoreError("Couldn't load more questions. Reload to try again.");
        setBankLoading(false);
        setBankLoadingMore(false);
      });
    // key is only carried on the paging object; activeFilter changes with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeReady, lessonId, standardCode, filterKey]);

  // A new scope or filter starts a fresh list from offset 0.
  useEffect(() => {
    if (!scopeReady) return;
    bankPagingRef.current = { offset: 0, loading: false, done: false };
    setBankQuestions([]);
    setBankTotal(0);
    setBankError(null);
    setBankMoreError(null);
    loadNextBankPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeReady, lessonId, standardCode, filterKey]);

  // Sentinel below the grid: scrolling it into view pulls the next page, the
  // same mechanic Session History uses. A callback ref re-attaches the observer
  // when the sentinel unmounts with the tab.
  const observerRef = useRef(null);
  const loadNextRef = useRef(loadNextBankPage);
  useEffect(() => { loadNextRef.current = loadNextBankPage; }, [loadNextBankPage]);
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node || typeof IntersectionObserver === "undefined") return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadNextRef.current(); },
      { rootMargin: "200px", threshold: 0.1 },
    );
    observerRef.current.observe(node);
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);

  // The set can hold questions outside the current filter — seed the join map
  // with one unfiltered pass when the group already has a set.
  useEffect(() => {
    if (!scopeReady || !questionSetId) return undefined;
    let cancelled = false;
    fetchAllScopedQuestions({ lessonId, standardCode, filter: { standardCode: pinnedStandardCode || undefined } })
      .then(({ questions }) => {
        if (cancelled) return;
        questions.forEach((q) => questionsByIdRef.current.set(q.id, q));
        setJoinVersion((v) => v + 1);
      })
      .catch(() => { /* join stays partial; the filtered fetch already logged */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeReady, questionSetId]);

  // Facets feed the filter dropdowns, scoped like the questions fetch.
  const facetsQuery = {
    standardCode: pinnedStandardCode || undefined,
    standards: pinnedStandardCode ? undefined : filter.standards,
    learningComponents: filter.learningComponents,
    questionType: filter.questionType,
  };
  const lessonFacets = useQuestionFacets(scopeReady && lessonId ? lessonId : null, facetsQuery);
  const standardFacets = useStandardQuestionFacets(scopeReady && !lessonId ? standardCode : null, facetsQuery);
  const facetsState = lessonId ? lessonFacets : standardFacets;

  const toggleQuestion = (q) => {
    setSelection((prev) => (
      prev.some((s) => s.challenge_id === q.id)
        ? prev.filter((s) => s.challenge_id !== q.id)
        : [...prev, toChallengeRef(q)]
    ));
  };

  // "Add All" means every question matching the filter, not just the pages
  // scrolled so far — so it drains the bank once, on demand.
  const [addingAll, setAddingAll] = useState(false);
  const addAll = async () => {
    setAddingAll(true);
    let all = bankQuestions;
    try {
      ({ questions: all } = await fetchAllScopedQuestions({ lessonId, standardCode, filter: activeFilter }));
      all.forEach((q) => questionsByIdRef.current.set(q.id, q));
      setJoinVersion((v) => v + 1);
    } catch (e) {
      logEducatorDashboard("QuestionSetView: failed to load the full bank for Add All", {
        level: LogLevel.WARNING,
        event: "bank_add_all_error",
        statusCode: e?.status,
        lessonId,
        standardCode,
      });
      setBankMoreError("Couldn't load the rest of the bank — added the questions loaded so far.");
    }
    setSelection((prev) => {
      const have = new Set(prev.map((s) => s.challenge_id));
      return [...prev, ...all.filter((q) => !have.has(q.id)).map(toChallengeRef)];
    });
    setAddingAll(false);
  };

  const setQuestions = useMemo(
    () => selection.map((s) => questionsByIdRef.current.get(s.challenge_id)).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection, bankQuestions],
  );

  // ---- Save & Exit ----
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  // After a save with same-topic siblings: { setId, count } offers the new set,
  // { filter, count, setLabel } offers the just-saved filter (empty-set save,
  // the carousel's ReuseFilterModal semantics).
  const [reuseOffer, setReuseOffer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const goBackToGroups = useCallback(() => {
    const path = constants.EDUCATOR_PATH;
    history.push(classId ? `${path}?classId=${classId}` : path);
  }, [history, classId]);

  // Other groups on the same topic (track + standard) in this bundle — the reuse
  // modal's natural offer list.
  const reuseCandidates = useMemo(
    () => bundleAssignments
      .filter((a) => a.group_code !== groupCode
        && a.track_type === trackType
        && a.standard_code === standardCode
        && a.track_type !== "no_assignment")
      .map((a) => a.group_code)
      // Ascending, numeric-aware: SAGY3 before SAGY14, not after it.
      .sort(compareCode),
    [bundleAssignments, groupCode, trackType, standardCode],
  );

  const confirmSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const filterState = isNoOpFilter(filter) ? null : serializeQuestionFilter(filter);
      if (selection.length === 0 && quickGroup?.id) {
        // Quick group: no assignment row to clear through, so the group's own PATCH
        // drops its set pointer. The group falls back to its filter, so the panel's
        // current filter is persisted alongside — null (nothing narrowed) clears it
        // and the group draws from the full bank again.
        await request(`quick-groups/classrooms/${classroomId}/${quickGroup.id}`, {
          method: "PATCH",
          payload: { question_set_id: null, question_filter: filterState },
        });
        trackClickedButton("Clear Question Set", { page_name: "Question Set", class: classroomId || null });
        goBackToGroups();
        return;
      }
      if (selection.length === 0) {
        // No custom set means the group falls back to its filter: the delta PATCH
        // drops the set pointer and stores the panel's current filter — null
        // (nothing narrowed) widens the group back to the standard's / lesson's
        // full pool. (A POST with no questions would 422, so this path never
        // creates a set.)
        await patchGroupAssignments(classroomId, assignmentId, [{
          group_code: groupCode,
          track_type: trackType,
          standard_code: standardCode,
          question_filter: filterState,
        }]);
        trackClickedButton("Clear Question Set", { page_name: "Question Set", class: classroomId || null });
        if (reuseCandidates.length > 0) {
          // No set to offer, so the siblings are offered the just-saved filter —
          // null included, which widens them back to the full bank.
          setIsUpdateModalOpen(false);
          setReuseOffer({
            filter: filterState,
            count: bankTotal,
            setLabel: filterState ? "Filtered Questions" : "Full Question Bank",
          });
        } else {
          goBackToGroups();
        }
        return;
      }
      const created = await createQuestionSet(classroomId, {
        group_code: groupCode,
        questions: selection,
        filter_state: filterState,
        auto_assign_early_finishers: !!filter.autoAssignEarlyFinishers,
      });
      trackClickedButton("Save Question Set", {
        page_name: "Question Set",
        class: classroomId || null,
        question_count: selection.length,
      });
      setIsUpdateModalOpen(false);
      if (created?.id && reuseCandidates.length > 0) {
        setReuseOffer({ setId: created.id, count: selection.length });
      } else {
        goBackToGroups();
      }
    } catch (e) {
      logEducatorDashboard("QuestionSetView: failed to save question set", {
        level: LogLevel.WARNING,
        event: "question_set_save_error",
        statusCode: e?.status,
        classroomId,
        groupCode,
      });
      setSaveError(e?.detail || e?.message || "Failed to save question set.");
    } finally {
      setSaving(false);
    }
  };

  const confirmReuse = async (codes) => {
    if (codes.length === 0) { goBackToGroups(); return; }
    setSaving(true);
    setSaveError(null);
    try {
      if (reuseOffer.setId) {
        await applyQuestionSet(reuseOffer.setId, codes);
        trackClickedButton("Apply Question Set", {
          page_name: "Question Set",
          class: classroomId || null,
          group_count: codes.length,
        });
      } else {
        await patchGroupAssignments(classroomId, assignmentId, codes.map((code) => ({
          group_code: code,
          track_type: trackType,
          standard_code: standardCode,
          question_filter: reuseOffer.filter,
        })));
        trackClickedButton("Apply Question Filter", {
          page_name: "Question Set",
          class: classroomId || null,
          group_count: codes.length,
        });
      }
      goBackToGroups();
    } catch (e) {
      logEducatorDashboard("QuestionSetView: failed to apply to sibling groups", {
        level: LogLevel.WARNING,
        event: reuseOffer?.setId ? "question_set_apply_error" : "question_filter_reuse_error",
        statusCode: e?.status,
        questionSetId: reuseOffer?.setId || null,
        groupCount: codes.length,
      });
      setSaveError(e?.detail || e?.message || "Failed to update the selected groups.");
      setSaving(false);
    }
  };

  // ---- Header text ----
  const lessonLabel = lesson
    ? [lesson.course_name, lesson.unit_ordinal_name, lesson.ordinal_name].filter(Boolean).join(", ")
    : null;
  const eyebrow = lessonLabel || (standardCode ? `STANDARD - ${standardCode}` : "");
  const title = lesson?.name || row?.standard_name || standardCode || "";
  const [narrativeExpanded, setNarrativeExpanded] = useState(false);
  // learning_goals arrives newline-joined (one goal per line), same as the
  // lesson detail panel reads it.
  const learningGoals = Array.isArray(lesson?.learning_goals)
    ? lesson.learning_goals
    : (lesson?.learning_goals || "").split("\n").map((g) => g.trim()).filter(Boolean);
  const narrative = lesson?.lesson_narrative || null;

  if (envelopeLoading && !row) {
    return (
      <Page>
        <StateMessage role="status">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p style={{ marginTop: 12 }}>Loading…</p>
        </StateMessage>
      </Page>
    );
  }
  if (!row) {
    return (
      <Page>
        <StateMessage role="alert">This group could not be found. <ShowMoreButton onClick={goBackToGroups}>Back to Groups</ShowMoreButton></StateMessage>
      </Page>
    );
  }

  const showStandardsFilter = Boolean(lessonId) && trackType === "core";
  const bankCount = bankTotal;
  const setCount = selection.length;

  return (
    <Page>
      <HeaderRow>
        <PieIcon />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <PageTitle>{title}</PageTitle>
          {learningGoals.length > 0 && (
            <>
              <NarrativeHeading>Learning Goals</NarrativeHeading>
              <GoalsList>
                {learningGoals.map((goal, i) => (
                  <li key={i}>{goal}</li>
                ))}
              </GoalsList>
            </>
          )}
          {narrative && (
            <>
              <NarrativeHeading>Lesson Narrative</NarrativeHeading>
              <NarrativeText id="lesson-narrative" $clamped={!narrativeExpanded}>{narrative}</NarrativeText>
              <ShowMoreButton
                type="button"
                aria-controls="lesson-narrative"
                aria-expanded={narrativeExpanded}
                onClick={() => setNarrativeExpanded((v) => !v)}
              >
                {narrativeExpanded ? "Show Less" : "Show More"}
              </ShowMoreButton>
            </>
          )}
        </div>
      </HeaderRow>

      <Section>
        <TabBar role="tablist">
          <TabButton
            type="button"
            role="tab"
            aria-selected={tab === "bank"}
            $active={tab === "bank"}
            onClick={() => setTab("bank")}
          >
            Question Bank
          </TabButton>
          <TabButton
            type="button"
            role="tab"
            aria-selected={tab === "set"}
            $active={tab === "set"}
            onClick={() => setTab("set")}
          >
            My Question Set ({setCount})
          </TabButton>
        </TabBar>

        <Body>
          {tab === "bank" && (
            <QuestionFilterPanel
              filter={filter}
              onChange={setFilter}
              facets={facetsState.facets}
              hasLoaded={facetsState.hasLoaded}
              error={facetsState.error}
              showStandards={showStandardsFilter}
              showAutoAssign
            />
          )}
          {/* The set tab keeps the auto-assign choice (Figma 5141-85544) without
              the filter row — same state, saved with the set either way. */}
          {tab === "set" && (
            <AutoAssignCheckbox
              checked={!!filter.autoAssignEarlyFinishers}
              onChange={(autoAssignEarlyFinishers) => setFilter((f) => ({ ...f, autoAssignEarlyFinishers }))}
            />
          )}

          <CountRow>
            <CountText>
              {tab === "bank" ? `${bankCount} Questions` : `${setCount} Questions`}
            </CountText>
            {tab === "bank" ? (
              <SecondaryButton
                type="button"
                onClick={addAll}
                disabled={bankLoading || addingAll || bankQuestions.length === 0}
              >
                {addingAll ? "Adding…" : "Add All to My Question Set"}
              </SecondaryButton>
            ) : (
              setCount > 0 && (
                <SecondaryButton type="button" onClick={() => setSelection([])}>
                  Clear Question Set
                </SecondaryButton>
              )
            )}
          </CountRow>

          {tab === "bank" && bankLoading && (
            <StateMessage role="status">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p style={{ marginTop: 12 }}>Loading questions…</p>
            </StateMessage>
          )}
          {tab === "bank" && !bankLoading && bankError && (
            <StateMessage role="alert" style={{ color: "crimson" }}>Error: {bankError}</StateMessage>
          )}
          {tab === "bank" && !bankLoading && !bankError && (
            <>
            <Grid>
              {bankQuestions.map((q) => {
                const added = selectedIds.has(q.id);
                return (
                  <Thumb key={q.id}>
                    <ThumbStage>
                      <ConsensusActivityRender
                        challenge={buildPreviewChallenge(q)}
                        disableOptions={true}
                        isMathJaxSet={false}
                        onMCAnswer={() => {}}
                        onORAnswer={() => {}}
                        showBorder={false}
                        showPromptFormula
                      />
                    </ThumbStage>
                    <HeartButton type="button" $added={added} onClick={() => toggleQuestion(q)} aria-pressed={added}>
                      <HeartIcon filled={added} />
                      {added ? "Added to My Question Set" : "Add to My Question Set"}
                    </HeartButton>
                  </Thumb>
                );
              })}
            </Grid>
            <LoadMoreTrigger ref={sentinelRef} />
            {bankLoadingMore && (
              <StateMessage role="status">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span style={{ marginLeft: 8 }}>Loading more questions…</span>
              </StateMessage>
            )}
            {bankMoreError && (
              <StateMessage role="alert" style={{ color: "crimson" }}>{bankMoreError}</StateMessage>
            )}
            </>
          )}

          {tab === "set" && setCount === 0 && (
            <EmptyState>
              <EmptyHeartIcon />
              <EmptyHeading>No question selected yet</EmptyHeading>
              <EmptyBody>
                Review questions in the Question Bank and add the ones you want your students to work on to My
                Question Set.
              </EmptyBody>
            </EmptyState>
          )}
          {tab === "set" && setCount > 0 && (
            <Grid>
              {setQuestions.map((q) => (
                <Thumb key={q.id}>
                  <ThumbStage>
                    <ConsensusActivityRender
                      challenge={buildPreviewChallenge(q)}
                      disableOptions={true}
                      isMathJaxSet={false}
                      onMCAnswer={() => {}}
                      onORAnswer={() => {}}
                      showBorder={false}
                      showPromptFormula
                    />
                  </ThumbStage>
                  <HeartButton type="button" $added onClick={() => toggleQuestion(q)} aria-pressed>
                    <HeartIcon filled />
                    Added to My Question Set
                  </HeartButton>
                </Thumb>
              ))}
            </Grid>
          )}
        </Body>
      </Section>

      <SaveBar>
        <SecondaryButton type="button" onClick={goBackToGroups}>
          Back to Groups
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={() => { setSaveError(null); setIsUpdateModalOpen(true); }}
        >
          Save &amp; Exit
        </PrimaryButton>
      </SaveBar>

      <UpdateQuestionSetModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        assignment={row}
        bundle={lessonEntry || {}}
        questionCount={setCount}
        saving={saving}
        error={saveError}
        onConfirm={confirmSave}
      />

      <ReuseQuestionSetModal
        isOpen={!!reuseOffer}
        onClose={goBackToGroups}
        assignment={row}
        bundle={lessonEntry || {}}
        questionCount={reuseOffer?.count}
        setLabel={reuseOffer?.setLabel}
        groupCodes={reuseCandidates}
        saving={saving}
        error={saveError}
        onConfirm={confirmReuse}
      />
    </Page>
  );
};
